import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

type Col = { title: string; links: { label: string; href: string; slug?: string }[] };

const columns: Col[] = [
  {
    title: "USE CASES",
    links: [
      { label: "For Renters", href: "/lease-decoder" },
      { label: "For Freelancers", href: "/freelancer-contracts" },
      { label: "For Entrepreneurs", href: "/" },
      { label: "For HR Managers", href: "/" },
      { label: "For Home Buyers", href: "/" },
    ],
  },
  {
    title: "DOCUMENT TYPES",
    links: [
      { label: "Lease Agreement", href: "/document-types/$slug", slug: "lease-agreement" },
      { label: "NDA", href: "/document-types/$slug", slug: "nda" },
      { label: "Employment Contract", href: "/document-types/$slug", slug: "employment-contract" },
      { label: "Service Agreement", href: "/document-types/$slug", slug: "service-agreement" },
      { label: "Terms of Service", href: "/document-types/$slug", slug: "terms-of-service" },
      { label: "View All 80+ →", href: "/document-types" },
    ],
  },
  {
    title: "COMPANY",
    links: [
      { label: "About", href: "/about" },
      { label: "Blog", href: "/blog" },
      { label: "Pricing", href: "/pricing" },
      { label: "Contact", href: "/contact" },
      { label: "Affiliates", href: "/affiliates" },
    ],
  },
  {
    title: "TRUST & LEGAL",
    links: [
      { label: "🔒 Security", href: "/" },
      { label: "🔐 Privacy Policy", href: "/privacy" },
      { label: "📜 Terms of Service", href: "/terms" },
      { label: "🍪 Cookie Policy", href: "/" },
      { label: "GDPR Compliance", href: "/" },
    ],
  },
];

const socials = [
  { label: "X", href: "https://twitter.com", icon: "𝕏" },
  { label: "LinkedIn", href: "https://linkedin.com", icon: "in" },
  { label: "Product Hunt", href: "https://producthunt.com", icon: "P" },
];

function FooterColumn({ col }: { col: Col }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#374151] pb-4 sm:border-0 sm:pb-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between py-2 text-left sm:cursor-default sm:py-0"
      >
        <h4 className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#D1D5DB]">
          {col.title}
        </h4>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-[#D1D5DB] transition-transform sm:hidden",
            open && "rotate-180",
          )}
        />
      </button>
      <ul className={cn("mt-3 space-y-2", !open && "hidden sm:block")}>
        {col.links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.href as any}
              params={l.slug ? ({ slug: l.slug } as any) : undefined}
              className="text-[13px] text-[#D1D5DB] transition-colors hover:text-white"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#111827] text-white">
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Logo variant="light" asLink={false} />
            <p className="mt-3 text-[13px] leading-relaxed text-[#D1D5DB]">
              AI-powered legal document decoder. Understand what you're signing.
            </p>
            <p className="mt-4 text-[12px] text-[#D1D5DB]">
              © {new Date().getFullYear()} legaldecoder
            </p>
          </div>

          {columns.map((col) => (
            <FooterColumn key={col.title} col={col} />
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-10 rounded-xl border border-[#374151] bg-white/[0.02] p-4">
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-[#D1D5DB]">
            ⚠️ Not Legal Advice. For Informational Purposes Only.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center gap-4 border-t border-[#374151] pt-6">
          <div className="flex items-center gap-3">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#374151] text-sm font-semibold text-[#D1D5DB] transition-opacity hover:opacity-80"
              >
                {s.icon}
              </a>
            ))}
          </div>
          <p className="text-center text-[12px] text-[#D1D5DB]">
            Made with ❤️ for people who don't speak legalese
          </p>
        </div>
      </div>
    </footer>
  );
}
